import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { parseLogsSearch, ParsedSearch } from '../../utils/logsSearchParser';
import { formatPodName } from '../../utils/formatPodName';

export type TimePreset = '5m' | '15m' | '1h' | '6h' | '24h' | '7d' | 'custom';
export type LogLevel = 'INFO' | 'WARN' | 'ERROR';

export interface LogsToolbarState {
  searchInput: string;
  timePreset: TimePreset;
  customSince: string;      // datetime-local (YYYY-MM-DDTHH:mm) — utilisé si timePreset='custom'
  levels: LogLevel[];
  pod: string;              // '' = tous
  container: string;        // '' = tous
  liveTail: boolean;
}

export const DEFAULT_TOOLBAR_STATE: LogsToolbarState = {
  searchInput: '',
  timePreset: '15m',
  customSince: '',
  levels: [],
  pod: '',
  container: '',
  liveTail: true,
};

const TIME_PRESETS: { key: TimePreset; label: string }[] = [
  { key: '5m', label: '5m' },
  { key: '15m', label: '15m' },
  { key: '1h', label: '1h' },
  { key: '6h', label: '6h' },
  { key: '24h', label: '24h' },
  { key: '7d', label: '7d' },
  { key: 'custom', label: '...' },
];

const LEVELS: LogLevel[] = ['INFO', 'WARN', 'ERROR'];

interface Props {
  state: LogsToolbarState;
  onChange: (next: LogsToolbarState) => void;
  onApply: () => void;
  onDownload?: () => void;
  pods?: Array<{ name: string }>;
  loading?: boolean;
  downloading?: boolean;
}

const LogsToolbar: React.FC<Props> = ({ state, onChange, onApply, onDownload, pods = [], loading = false, downloading = false }) => {
  const { t } = (useTranslation as any)('deployments');

  const parsed = useMemo(() => parseLogsSearch(state.searchInput), [state.searchInput]);

  const set = <K extends keyof LogsToolbarState>(key: K, value: LogsToolbarState[K]) => {
    onChange({ ...state, [key]: value });
  };

  const toggleLevel = (lvl: LogLevel) => {
    const has = state.levels.includes(lvl);
    set('levels', has ? state.levels.filter(l => l !== lvl) : [...state.levels, lvl]);
  };

  const onSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onApply();
    }
  };

  return (
    <div className="px-6 py-3 bg-surface-0 border-b border-border-1 space-y-3">
      {/* Row 1 — Search bar unifiée + apply */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <svg
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-quaternary"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            value={state.searchInput}
            onChange={(e) => set('searchInput', e.target.value)}
            onKeyDown={onSearchKeyDown}
            placeholder={t('logs.searchPlaceholder') as string}
            className="w-full bg-surface-2 border border-border-1 rounded-input pl-9 pr-3 py-2 text-sm text-text-primary placeholder-text-quaternary focus:outline-none focus:ring-1 focus:ring-accent-500 focus:border-accent-500"
          />
        </div>
        <button
          type="button"
          className="text-text-quaternary hover:text-text-tertiary text-xs px-2"
          title={t('logs.searchHelpTitle') as string}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093M12 17h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </button>
        <button
          type="button"
          onClick={onApply}
          disabled={loading}
          className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-md text-white bg-accent-600 hover:bg-accent-700 disabled:bg-accent-400 disabled:cursor-not-allowed transition-colors"
        >
          {loading ? t('logs.loading') : t('logs.apply')}
        </button>
        {onDownload && (
          <button
            type="button"
            onClick={onDownload}
            disabled={downloading}
            title={t('logs.downloadTitle') as string}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md text-text-secondary bg-surface-2 border border-border-1 hover:bg-surface-3 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3" />
            </svg>
            {downloading ? t('logs.downloading') : t('logs.download')}
          </button>
        )}
      </div>

      {/* Chip récap — décompose ce qui a été parsé pour transparence */}
      {(parsed.terms.length + parsed.excludes.length + Object.keys(parsed.labels).length + (parsed.since ? 1 : 0)) > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 text-xs">
          {parsed.terms.map((term, i) => (
            <span key={`t-${i}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-badge bg-accent-500/12 text-accent-400 font-mono">
              <span className="opacity-60">search:</span> {term}
            </span>
          ))}
          {parsed.excludes.map((term, i) => (
            <span key={`x-${i}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-badge bg-red-500/12 text-red-400 font-mono">
              <span className="opacity-60">exclude:</span> {term}
            </span>
          ))}
          {Object.entries(parsed.labels).map(([k, v]) => (
            <span key={`l-${k}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-badge bg-surface-3 text-text-secondary font-mono">
              <span className="opacity-60">{k}=</span>{v}
            </span>
          ))}
          {parsed.since && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-badge bg-surface-3 text-text-secondary font-mono">
              <span className="opacity-60">last=</span>{parsed.since}
            </span>
          )}
        </div>
      )}

      {/* Row 2 — Time picker + Levels + Pod/Container + Live tail toggle */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        {/* Time picker presets */}
        <div className="flex items-center gap-1">
          <span className="text-xs text-text-tertiary mr-1">{t('logs.time')}:</span>
          {TIME_PRESETS.map(p => (
            <button
              key={p.key}
              type="button"
              onClick={() => set('timePreset', p.key)}
              className={`px-2 py-1 text-xs rounded border transition-colors ${
                state.timePreset === p.key
                  ? 'bg-accent-500/12 border-accent-500 text-accent-400'
                  : 'bg-surface-2 border-border-1 text-text-secondary hover:bg-surface-3'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>

        {state.timePreset === 'custom' && (
          <input
            type="datetime-local"
            value={state.customSince}
            onChange={(e) => set('customSince', e.target.value)}
            className="bg-surface-2 border border-border-1 rounded-input px-2 py-1 text-xs text-text-primary focus:outline-none focus:ring-1 focus:ring-accent-500"
          />
        )}

        {/* Level checkboxes */}
        <div className="flex items-center gap-2">
          <span className="text-xs text-text-tertiary">{t('logs.level')}:</span>
          {LEVELS.map(lvl => (
            <label key={lvl} className="inline-flex items-center gap-1 cursor-pointer text-xs">
              <input
                type="checkbox"
                checked={state.levels.includes(lvl)}
                onChange={() => toggleLevel(lvl)}
                className="w-3.5 h-3.5 accent-accent-500"
              />
              <span className={
                lvl === 'ERROR' ? 'text-red-400' :
                lvl === 'WARN'  ? 'text-yellow-400' :
                                  'text-text-secondary'
              }>{lvl}</span>
            </label>
          ))}
        </div>

        {/* Pod selector */}
        <div className="flex items-center gap-1">
          <span className="text-xs text-text-tertiary">{t('logs.pod')}:</span>
          <select
            value={state.pod}
            onChange={(e) => set('pod', e.target.value)}
            className="bg-surface-2 border border-border-1 rounded-input px-2 py-1 text-xs text-text-primary focus:outline-none focus:ring-1 focus:ring-accent-500 max-w-[220px]"
          >
            <option value="">{t('logs.allPods')}</option>
            {pods.map(p => (
              <option key={p.name} value={p.name} title={p.name}>{formatPodName(p.name)}</option>
            ))}
          </select>
        </div>

        {/* Container input (libre — l'utilisateur tape s'il connaît le nom) */}
        <div className="flex items-center gap-1">
          <span className="text-xs text-text-tertiary">{t('logs.container')}:</span>
          <input
            type="text"
            value={state.container}
            onChange={(e) => set('container', e.target.value)}
            placeholder={t('logs.allContainers') as string}
            className="bg-surface-2 border border-border-1 rounded-input px-2 py-1 text-xs text-text-primary placeholder-text-quaternary focus:outline-none focus:ring-1 focus:ring-accent-500 w-32"
          />
        </div>

        {/* Live tail toggle */}
        <label className="inline-flex items-center gap-2 cursor-pointer ml-auto">
          <input
            type="checkbox"
            checked={state.liveTail}
            onChange={(e) => set('liveTail', e.target.checked)}
            className="sr-only peer"
          />
          <div className="relative w-9 h-5 bg-surface-4 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-border-1 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-green-600"></div>
          <span className="text-xs text-text-secondary">
            {t('logs.liveTail')}
            {state.liveTail && (
              <span className="ml-1 inline-block w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse align-middle" />
            )}
          </span>
        </label>
      </div>
    </div>
  );
};

export default LogsToolbar;
